export interface Location {
  id: string
  name: string
  country: string
  latitude: number
  longitude: number
  createdUtc: string // ISO timestamp
  updatedUtc: string | null
}

// What create and update send. Mirrors LocationInput on the API: no id, no timestamps.
export interface LocationInput {
  name: string
  country: string
  latitude: number
  longitude: number
}

// Empty base => relative URLs. Same reasoning as api/weather.ts: CloudFront serves
// /api/* from this origin in production, Vite proxies it in development.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// The API returns a problem-details body on a 400 from model validation. Surfacing the
// field messages beats "Locations API returned 400", which tells the user nothing about
// which field they got wrong.
async function failure(res: Response): Promise<Error> {
  try {
    const body = await res.json()
    const errors = body?.errors as Record<string, string[]> | undefined

    if (errors) {
      const detail = Object.values(errors).flat().join(' ')
      if (detail) return new Error(detail)
    }

    if (typeof body?.title === 'string') return new Error(body.title)
  } catch {
    // Not JSON, or an empty body — fall through to the status code.
  }

  return new Error(`Locations API returned ${res.status}`)
}

export async function listLocations(): Promise<Location[]> {
  const res = await fetch(`${API_BASE}/api/locations`)
  if (!res.ok) throw await failure(res)
  return (await res.json()) as Location[]
}

export async function createLocation(input: LocationInput): Promise<Location> {
  const res = await fetch(`${API_BASE}/api/locations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await failure(res)
  return (await res.json()) as Location
}

export async function updateLocation(id: string, input: LocationInput): Promise<Location> {
  const res = await fetch(`${API_BASE}/api/locations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await failure(res)
  return (await res.json()) as Location
}

export async function deleteLocation(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/locations/${id}`, { method: 'DELETE' })
  if (!res.ok) throw await failure(res)
}
